/**
 * Role-Based Access Control (RBAC) Configuration
 * 
 * This file centralizes all role-permission mappings for the application.
 * When a new role is added, simply add a new entry in ROLE_PERMISSIONS
 * with the features/actions that role should have access to.
 */

// ─── Feature Keys ────────────────────────────────────────────────────────────
// Each feature key represents a distinct capability or UI element in the system.

export const FEATURES = {
  // Navigation Tabs
  TAB_OVERVIEW: 'TAB_OVERVIEW',
  TAB_CERTIFICATE_REQUESTS: 'TAB_CERTIFICATE_REQUESTS',
  TAB_ISSUED_CERTIFICATES: 'TAB_ISSUED_CERTIFICATES',
  TAB_ASSIGNED_BOOKLETS: 'TAB_ASSIGNED_BOOKLETS',
  TAB_MANAGE_USERS: 'TAB_MANAGE_USERS',

  // Home Page Widgets
  HOME_PENDING_REQUESTS: 'HOME_PENDING_REQUESTS',
  HOME_ISSUED_CERTIFICATES: 'HOME_ISSUED_CERTIFICATES',
  HOME_BAR_CHART: 'HOME_BAR_CHART',
  HOME_PENDING_DELIVERY_SUMMARY: 'HOME_PENDING_DELIVERY_SUMMARY',

  // Certificate Request Actions
  ACTION_APPROVE_REQUEST: 'ACTION_APPROVE_REQUEST',
  ACTION_REJECT_REQUEST: 'ACTION_REJECT_REQUEST',
  ACTION_DELIVER_CERTIFICATE: 'ACTION_DELIVER_CERTIFICATE',
  ACTION_CANCEL_CERTIFICATE: 'ACTION_CANCEL_CERTIFICATE',

  // Booklet Management
  ACTION_ASSIGN_BOOKLET: 'ACTION_ASSIGN_BOOKLET',
  ACTION_DELETE_BOOKLET: 'ACTION_DELETE_BOOKLET',

  // User Management
  ACTION_MANAGE_USERS: 'ACTION_MANAGE_USERS',
  ACTION_ASSIGN_ROLE_ISSUER: 'ACTION_ASSIGN_ROLE_ISSUER',
  ACTION_ASSIGN_ROLE_APPROVER: 'ACTION_ASSIGN_ROLE_APPROVER',
  ACTION_ASSIGN_ROLE_VIEWER: 'ACTION_ASSIGN_ROLE_VIEWER',

  // Misc UI
  GUIDED_TOUR: 'GUIDED_TOUR',
  VIEW_SUBORDINATE_REQUESTS: 'VIEW_SUBORDINATE_REQUESTS',
  SWITCH_ROLES: 'SWITCH_ROLES',
} as const;

export type Feature = (typeof FEATURES)[keyof typeof FEATURES];

// ─── Role Permission Map ─────────────────────────────────────────────────────
// Maps each role to its allowed features. If a feature is not listed, it's denied.

export const ROLE_PERMISSIONS: Record<string, Feature[]> = {
  ISSUER: [
    FEATURES.TAB_OVERVIEW,
    FEATURES.TAB_CERTIFICATE_REQUESTS,
    FEATURES.HOME_PENDING_REQUESTS,
    FEATURES.HOME_ISSUED_CERTIFICATES,
    FEATURES.HOME_PENDING_DELIVERY_SUMMARY,
    FEATURES.ACTION_APPROVE_REQUEST,
    FEATURES.ACTION_REJECT_REQUEST,
    FEATURES.ACTION_DELIVER_CERTIFICATE,
    FEATURES.ACTION_CANCEL_CERTIFICATE,
    FEATURES.GUIDED_TOUR,
    FEATURES.VIEW_SUBORDINATE_REQUESTS,
    FEATURES.SWITCH_ROLES,
  ],

  APPROVER: [
    FEATURES.TAB_OVERVIEW,
    FEATURES.TAB_CERTIFICATE_REQUESTS,
    FEATURES.TAB_ASSIGNED_BOOKLETS,
    FEATURES.HOME_PENDING_REQUESTS,
    FEATURES.HOME_ISSUED_CERTIFICATES,
    FEATURES.HOME_BAR_CHART,
    FEATURES.HOME_PENDING_DELIVERY_SUMMARY,
    FEATURES.ACTION_APPROVE_REQUEST,
    FEATURES.ACTION_REJECT_REQUEST,
    FEATURES.ACTION_DELIVER_CERTIFICATE,
    FEATURES.ACTION_CANCEL_CERTIFICATE,
    FEATURES.ACTION_ASSIGN_BOOKLET,
    FEATURES.ACTION_MANAGE_USERS,
    FEATURES.ACTION_ASSIGN_ROLE_ISSUER,
    FEATURES.ACTION_ASSIGN_ROLE_VIEWER,
    FEATURES.GUIDED_TOUR,
    FEATURES.VIEW_SUBORDINATE_REQUESTS,
    FEATURES.SWITCH_ROLES,
  ],

  LICENSE_MANAGER: [
    FEATURES.TAB_OVERVIEW,
    FEATURES.TAB_ISSUED_CERTIFICATES,
    FEATURES.TAB_ASSIGNED_BOOKLETS,
    FEATURES.HOME_ISSUED_CERTIFICATES,
    FEATURES.HOME_BAR_CHART,
    FEATURES.HOME_PENDING_DELIVERY_SUMMARY,
    FEATURES.ACTION_ASSIGN_BOOKLET,
    FEATURES.ACTION_DELETE_BOOKLET,
    FEATURES.ACTION_MANAGE_USERS,
    FEATURES.ACTION_ASSIGN_ROLE_ISSUER,
    FEATURES.ACTION_ASSIGN_ROLE_APPROVER,
    FEATURES.ACTION_ASSIGN_ROLE_VIEWER,
    FEATURES.GUIDED_TOUR,
    FEATURES.SWITCH_ROLES,
  ],

  // Viewer Roles — read-only access
  CCMM: [
    FEATURES.TAB_OVERVIEW,
    FEATURES.TAB_ISSUED_CERTIFICATES,
    // FEATURES.HOME_ISSUED_CERTIFICATES,
    FEATURES.HOME_BAR_CHART,
    FEATURES.GUIDED_TOUR,
  ],

  UL: [
    FEATURES.TAB_OVERVIEW,
    FEATURES.TAB_ISSUED_CERTIFICATES,
    // FEATURES.HOME_ISSUED_CERTIFICATES,
    FEATURES.HOME_BAR_CHART,
    FEATURES.GUIDED_TOUR,
  ],

  CL: [
    FEATURES.TAB_OVERVIEW,
    FEATURES.TAB_ISSUED_CERTIFICATES,
    FEATURES.HOME_BAR_CHART,
  ],

  VIEWER: [
    FEATURES.TAB_OVERVIEW,
    FEATURES.TAB_ISSUED_CERTIFICATES,
    // FEATURES.HOME_ISSUED_CERTIFICATES,
    FEATURES.HOME_BAR_CHART,
    FEATURES.GUIDED_TOUR,
  ],
};

// ─── Helper Functions ────────────────────────────────────────────────────────

/**
 * Check if a role has access to a specific feature
 */
export const hasPermission = (role: string | undefined | null, feature: Feature): boolean => {
  if (!role) return false;
  const permissions = ROLE_PERMISSIONS[role.toUpperCase()];
  if (!permissions) return false;
  return permissions.includes(feature);
};

/**
 * Check if a role has access to ANY of the given features
 */
export const hasAnyPermission = (role: string | undefined | null, features: Feature[]): boolean => {
  if (!role) return false;
  return features.some((feature) => hasPermission(role, feature));
};

/**
 * Check if a role has access to ALL of the given features
 */
export const hasAllPermissions = (role: string | undefined | null, features: Feature[]): boolean => {
  if (!role) return false;
  return features.every((feature) => hasPermission(role, feature));
};

/**
 * Get all features for a role
 */
export const getPermissions = (role: string | undefined | null): Feature[] => {
  if (!role) return [];
  return ROLE_PERMISSIONS[role.toUpperCase()] || [];
};

/**
 * Check if a role is a viewer (read-only) role
 */
export const isViewerRole = (role: string | undefined | null): boolean => {
  if (!role) return false;
  const viewerRoles = ['CCMM', 'UL', 'CL', 'VIEWER'];
  return viewerRoles.includes(role.toUpperCase());
};
